import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "@/lib/auth-context";

/**
 * Landing page for an emailed vault magic link
 * (`/auth/verify?challenge_id=…&token=…`). Exchanges the link for a vault custom
 * token via the backend, signs in, then routes to the dashboard (`/`). The link
 * is single-use, so we guard against React StrictMode's double-invoke.
 */
export function VerifyRoute() {
  const { verifyMagicLink } = useAuth();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const challengeId = params.get("challenge_id");
    const token = params.get("token");

    if (!challengeId || !token) {
      setError(
        "This sign-in link is incomplete. Return to sign in and request a new one."
      );
      return;
    }

    verifyMagicLink(challengeId, token)
      .then(() => router.navigate({ to: "/" }))
      .catch((err) => {
        console.error("Failed to verify vault sign-in link:", err);
        setError(
          "This sign-in link is no longer valid. Return to sign in and request a new one."
        );
      });
  }, [router, verifyMagicLink]);

  return (
    <div style={{ minHeight: "100vh" }}>
      <main style={{ padding: "1.5rem", maxWidth: 720, margin: "0 auto" }}>
        <h1 style={{ fontFamily: "Poppins, sans-serif" }}>Signing you in…</h1>
        {!error && (
          <p style={{ color: "#6b7280" }}>
            Verifying your secure sign-in link. This only takes a moment.
          </p>
        )}
        {error && (
          <>
            <p style={{ color: "#b91c1c", marginTop: 12 }}>{error}</p>
            <p style={{ color: "#6b7280" }}>
              <a href="/" style={{ color: "var(--ds-color-primary-500)" }}>
                Return to sign in
              </a>{" "}
              to request a new link.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
